/**
 * Composite "activity" score for the lobby's default sort.
 *
 * Replaces the single-dimension "7D Rewards" / "Taxes" sorts with a
 * blended signal that captures the three independent ways a board
 * can be alive:
 *
 *   1. **Holders** (`uniqueHolderCount`) — distinct addresses that
 *      currently hold a seat. The cleanest "is anyone here?" signal
 *      we have on the lobby payload — chat-only success boards
 *      (yield-less, players paying tax just to be in chat) still
 *      need holders, where a yield-only sort would bury them at $0.
 *   2. **Recency** (`lastSeatPurchasedAtSec`) — when did anyone
 *      last care enough to buy in? A board with high lifetime
 *      numbers but no buys in 30d is functionally dead; a fresh
 *      board with modest cumulative numbers is rising. Folded in
 *      multiplicatively so a graduated, dormant board sinks below
 *      any lively one regardless of legacy stats.
 *   3. **Tax + chat heat** (`taxesUsd`, `chatMessages7d`) — both
 *      cumulative-ish "this is generating activity" signals,
 *      additively combined. Tax catches purely onchain success;
 *      chat catches off-chain success (rooms where holders show up
 *      because the conversation is good).
 *
 * The output is unit-less and only meaningful in comparison to other
 * boards in the same ranking pass — never displayed to users, never
 * persisted. The exported `ACTIVITY_SCORE_VERSION` is bumped when
 * the formula changes so cached client memos can invalidate cleanly
 * if we ever start round-tripping it through a server cache.
 */

import type { MockBoard } from './mock-boards';

/** Bump when the formula or weights change. Currently unused at
 *  runtime — exported so future cache layers / snapshot tests can
 *  key on it without re-deriving the constant. */
export const ACTIVITY_SCORE_VERSION = 1;

/** Half-life of the recency decay in days. A board last bought 14d
 *  ago contributes 0.5× recency weight; 28d → 0.25×. Tuned to feel
 *  right against the 7d "Hot" mental model the rest of the lobby
 *  uses (rewards7d, chatMessages7d) — boards that are truly fresh
 *  (last buy in last few days) dominate, but a 2-week-old board
 *  with continuing chat traffic still ranks well above a 60-day-
 *  dormant one. */
const RECENCY_HALF_LIFE_DAYS = 14;

/** Floor recency weight applied to boards that have *never* had a
 *  seat purchased (`lastSeatPurchasedAtSec === null`). Without this
 *  floor every brand-new board would multiply to 0 and be invisible
 *  no matter how many holders the curator/whitelist seeded. 0.2
 *  matches the recency multiplier of a board last bought ~32 days
 *  ago — pre-launch boards read as "moderately stale" rather than
 *  outright dead. */
const NEVER_BOUGHT_RECENCY_FLOOR = 0.2;

/**
 * Strip a USD-formatted string ("$1.2k", "$48", "$0") to a finite
 * number. Mirrors the `parseNum` helper inside `BoardDirectory.tsx`
 * but lives here so the score function is self-contained — the
 * sort comparator and the score function can drift on independent
 * formatters without one breaking the other.
 *
 * Empty / unparseable inputs return `0` so the score function can
 * always run; a literal `'$0'` (the canonical "nothing here yet"
 * value emitted by `formatTaxesUsd` / `formatYield7d`) cleanly
 * contributes 0 without special casing.
 */
function parseUsd(s: string | null | undefined): number {
  if (!s) return 0;
  const cleaned = s.replace(/^\$/, '').trim();
  const m = cleaned.match(/([\d,.]+)([km]?)/i);
  if (!m) return 0;
  const n = parseFloat(m[1].replace(/,/g, ''));
  if (!Number.isFinite(n)) return 0;
  const suffix = m[2]?.toLowerCase();
  const mul = suffix === 'k' ? 1_000 : suffix === 'm' ? 1_000_000 : 1;
  return n * mul;
}

/**
 * Recency multiplier in `[0, 1]` for a "last seat purchased at"
 * timestamp. Pure exponential decay with `RECENCY_HALF_LIFE_DAYS`
 * — `1.0` if the buy happened in the last second, `0.5` at the
 * half-life, asymptoting to 0 at infinity. Boards with no recorded
 * purchase get the `NEVER_BOUGHT_RECENCY_FLOOR` so they stay
 * eligible for ranking.
 */
function recencyMultiplier(
  lastSec: number | null,
  nowSec: number,
): number {
  if (lastSec === null || lastSec <= 0) return NEVER_BOUGHT_RECENCY_FLOOR;
  const ageSec = Math.max(0, nowSec - lastSec);
  const days = ageSec / 86_400;
  return Math.pow(0.5, days / RECENCY_HALF_LIFE_DAYS);
}

/**
 * Compute the composite activity score for a single board.
 *
 * Conceptually:
 *
 *   score = (holderTerm + chatTerm + taxTerm) * recencyMultiplier
 *
 * where each term is on a `log1p` scale to prevent any single
 * dimension blowing out the others. Without `log1p`, a board with
 * 10 000 holders would dwarf a board with 50 holders + heavy chat
 * traffic, even though the second is much more interesting from a
 * "where is something happening?" lens.
 *
 * The relative coefficients (50 / 25 / 5) encode the weighting:
 *   - **Holders** (×50) — strongest single signal of "this exists
 *     as a board with stakeholders".
 *   - **Chat** (×25) — direct measure of off-chain activity, the
 *     gap rewards-only sorts missed.
 *   - **Tax** (×5)  — useful as a tiebreak, but not allowed to
 *     dominate (that's what the dedicated "Taxes" sort is for).
 *
 * `nowSec` is passed in (not read off `Date.now()`) so the
 * comparator and the curated-rail freshness rules in
 * `BoardDirectory.tsx` can share a single per-render clock — this
 * prevents two boards near a recency boundary from being scored
 * with slightly different "now"s within the same sort pass.
 */
export function activityScore(board: MockBoard, nowSec: number): number {
  const holderTerm = Math.log1p(Math.max(0, board.uniqueHolderCount)) * 50;
  const chatTerm = Math.log1p(Math.max(0, board.chatMessages7d)) * 25;
  const taxTerm = Math.log1p(Math.max(0, parseUsd(board.taxesUsd))) * 5;
  const recency = recencyMultiplier(board.lastSeatPurchasedAtSec, nowSec);
  return (holderTerm + chatTerm + taxTerm) * recency;
}
